import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Twitter, Copy, Check, Crown } from "lucide-react";
import { useState } from "react";
import { fmtUSD, fmtUSDprecise, entityLabel, entityName, entityImage } from "@/lib/format";
import { toast } from "sonner";

export default function ShareModal({ result, entity, open, onClose }) {
  const [copied, setCopied] = useState(false);
  if (!result || !entity) return null;

  const label = entityLabel(entity);
  const newRank = result.new_rank;
  const isTopEvent = result.events.some((e) => e.type === "new_number_one" || e.type === "flip");
  const flipEvent = result.events.find((e) => e.type === "flip");

  const headline = flipEvent
    ? flipEvent.title
    : newRank === 1
    ? `${label} JUST TOOK THE CROWN 👑`
    : `${label} JUST MOVED TO #${newRank} 🔥`;

  const isSimulated = result.simulated || result.contribution.simulated;
  const shareText = isSimulated
    ? `${result.share.text}\n\n${label} is now #${newRank} on FLIP 🏆 @backonflip_`
    : `${result.share.text}\n\n${label} is now #${newRank} on FLIP 🏆 — verified on-chain ✓ @backonflip_`;
  const sharePage = new URL(
    `${entity.category === "tokens" ? "/token/" : "/caller/"}${entity.slug}`,
    window.location.origin
  );
  const shareNonce = (result.contribution?.transaction_hash || `${newRank}-${result.new_total}`)
    .replace(/[^A-Za-z0-9_-]/g, "")
    .slice(0, 100);
  sharePage.searchParams.set("share", shareNonce);
  const shareUrl = sharePage.toString();
  const xShareUrl = (() => {
    const url = new URL("https://x.com/intent/post");
    url.searchParams.set("text", shareText);
    url.searchParams.set("url", shareUrl);
    return url.toString();
  })();
  const onCopy = async () => {
    const text = `${shareText} ${shareUrl}`;
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const input = document.createElement("textarea");
      input.value = text;
      input.setAttribute("readonly", "");
      input.style.position = "fixed";
      input.style.opacity = "0";
      document.body.appendChild(input);
      input.select();
      document.execCommand("copy");
      input.remove();
    }
    setCopied(true);
    toast.success("Link copied");
    setTimeout(() => setCopied(false), 1600);
  };

  const img = entityImage(entity);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md gap-0 overflow-hidden border-white/10 bg-[#0a0a0a] p-0" data-testid="share-modal">
        {/* close = the native DialogContent X (data-testid="dialog-close") */}
        <DialogTitle className="sr-only">Share your FLIP result</DialogTitle>
        <DialogDescription className="sr-only">Share this leaderboard result on X or copy its link.</DialogDescription>

        {/* Generated social card */}
        <div className="relative overflow-hidden border-b border-white/10 bg-gradient-to-b from-[#0f0f0f] to-black px-6 pb-6 pt-8" data-testid="share-card">
          <div className="mb-5 flex items-center gap-1.5">
            <span className="font-display text-lg font-black tracking-tighter">FLIP</span>
            <span className="text-gold">↑</span>
          </div>
          <div className="mb-4 flex items-center gap-3">
            {img ? (
              <img src={img} alt={label} className="h-14 w-14 rounded-full border border-white/10 object-cover" />
            ) : (
              <div className="flex h-14 w-14 items-center justify-center rounded-full border border-white/10 bg-zinc-900 font-display text-lg font-black">
                {label.replace("@", "").slice(0, 2).toUpperCase()}
              </div>
            )}
            <div>
              <div className="font-display text-2xl font-black tracking-tight">{label}</div>
              <div className="text-sm text-zinc-500">{entityName(entity)}</div>
            </div>
          </div>

          <div className="font-display text-2xl font-black leading-tight tracking-tight">
            {headline}
          </div>

          <div className="mt-5 grid grid-cols-3 gap-3 border-t border-white/10 pt-4">
            <div>
              <div className="text-[9px] uppercase tracking-wider text-zinc-600">Rank</div>
              <div className="flex items-center gap-1 font-mono text-xl font-bold text-gold">
                {newRank === 1 && <Crown className="h-4 w-4 fill-gold" strokeWidth={1.5} />}#{newRank}
              </div>
            </div>
            <div>
              <div className="text-[9px] uppercase tracking-wider text-zinc-600">Total backed</div>
              <div className="font-mono text-xl font-bold">{fmtUSD(result.new_total)}</div>
            </div>
            <div>
              <div className="text-[9px] uppercase tracking-wider text-zinc-600">You backed</div>
              <div className="font-mono text-xl font-bold text-positive">{fmtUSDprecise(result.contribution.usd_value)}</div>
            </div>
          </div>
        </div>

        <div className="space-y-3 px-5 py-5">
          <div className="text-center">
            <div className="font-display text-base font-bold">Nice. You moved the leaderboard.</div>
            <div className="text-sm text-zinc-500">Share it and rally more backers.</div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <a
              data-testid="share-on-x"
              href={xShareUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 rounded-full bg-white py-3 text-sm font-bold text-black transition-transform hover:bg-zinc-200 active:scale-95"
            >
              <Twitter className="h-4 w-4" strokeWidth={2} /> Share on X
            </a>
            <button
              data-testid="share-copy-link"
              onClick={onCopy}
              className="flex items-center justify-center gap-2 rounded-full border border-white/15 py-3 text-sm font-bold transition-colors hover:bg-white/5 active:scale-95"
            >
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" strokeWidth={1.5} />} Copy link
            </button>
          </div>
          <div className="rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2 font-mono text-[10px] text-zinc-500">
            {isSimulated
              ? "Demo contribution · simulated (no funds moved)"
              : <>tx {result.contribution.transaction_hash.slice(0, 18)}… · verified on-chain ✓</>}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
